import { type Incident, IncidentStatus, renderParticipantName } from '@piccolohealth/pbs-common';
import { ChakraV3, FancyDate } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FaPencilAlt } from 'react-icons/fa';

import { FormSection } from '../../components/forms/FormSection';
import { FormStack } from '../../components/forms/FormStack';
import { showModal } from '../../components/generic/Modal';
import { UserDescription } from '../../components/user/UserDescription';
import { IncidentEditModal } from './IncidentEditModal';
import { IncidentSeverityTag } from './IncidentSeverityTag';
import { IncidentStatusTag } from './IncidentStatusTag';

export interface Props {
  incident: Incident;
}

export const IncidentCard = (props: Props) => {
  const { incident } = props;

  const isReviewed = incident.status === IncidentStatus.Reviewed;

  const witnessesContent = P.run(() => {
    if (P.isEmpty(incident.witnesses)) {
      return <ChakraV3.Text fontSize='sm'>No witnesses</ChakraV3.Text>;
    }

    return (
      <ChakraV3.Wrap gapX={12} gapY={4}>
        {incident.witnesses.map((witness, index) => (
          <ChakraV3.WrapItem key={`witness-${index}`}>
            <UserDescription
              name={P.compact([witness.name, witness.description]).join(' - ')}
              secondary={P.compact([witness.email, witness.phone]).join(' ')}
            />
          </ChakraV3.WrapItem>
        ))}
      </ChakraV3.Wrap>
    );
  });

  const reviewContentSummary = P.run(() => {
    if (incident.status !== IncidentStatus.Reviewed) {
      return null;
    }

    const reviewers = incident.reviewers.map((reviewer) => {
      return (
        <UserDescription
          key={reviewer.id}
          name={reviewer.name}
          secondary={reviewer.email}
          picture={reviewer.picture}
        />
      );
    });

    return (
      <>
        <ChakraV3.Separator />
        <FormSection heading='Review summary'>
          <FormStack orientation='vertical'>
            <ChakraV3.FormField label='Reviewed by'>{reviewers}</ChakraV3.FormField>
            <ChakraV3.FormField label='Actions to be taken'>
              <ChakraV3.Text whiteSpace='pre-wrap' fontSize='sm'>
                {incident.actions}
              </ChakraV3.Text>
            </ChakraV3.FormField>
          </FormStack>
        </FormSection>
      </>
    );
  });

  return (
    <ChakraV3.Card.Root h='full'>
      <ChakraV3.Card.Header>
        <ChakraV3.HStack align='stretch'>
          <ChakraV3.Stack gap={0}>
            <ChakraV3.Heading size='xl' fontWeight='bold'>
              Details
            </ChakraV3.Heading>
            <ChakraV3.Text fontSize='sm' color='secondary'>
              View and edit basic incident details here
            </ChakraV3.Text>
          </ChakraV3.Stack>
          <ChakraV3.Spacer />
        </ChakraV3.HStack>
      </ChakraV3.Card.Header>
      <ChakraV3.Card.Body>
        <ChakraV3.Stack gap={4} h='full'>
          <FormSection heading='Basic info'>
            <FormStack orientation='horizontal'>
              <ChakraV3.FormField label='Severity'>
                <IncidentSeverityTag severity={incident.severity} size='sm' w='fit-content' />
              </ChakraV3.FormField>
              <ChakraV3.FormField label='Status'>
                <IncidentStatusTag status={incident.status} size='sm' w='fit-content' />
              </ChakraV3.FormField>
            </FormStack>
            <FormStack orientation='horizontal'>
              <ChakraV3.FormField label='Clinic/Team location'>
                {incident.location ? (
                  <ChakraV3.Tag w='fit-content' size='sm' colorPalette={incident.location.color}>
                    {incident.location?.name}
                  </ChakraV3.Tag>
                ) : (
                  <ChakraV3.Text>-</ChakraV3.Text>
                )}
              </ChakraV3.FormField>
              <ChakraV3.FormField label='NDIS reportable'>
                <ChakraV3.Text fontSize='sm'>
                  {incident.ndisReportableIncident ? 'Yes' : 'No'}
                </ChakraV3.Text>
              </ChakraV3.FormField>
            </FormStack>
            <FormStack orientation='horizontal'>
              <ChakraV3.FormField label='Category'>
                <ChakraV3.Tag w='fit-content' size='sm' colorPalette={incident.category.color}>
                  {incident.category.name}
                </ChakraV3.Tag>
              </ChakraV3.FormField>
              <ChakraV3.FormField label='Labels'>
                {P.isEmpty(incident.labels) ? (
                  <ChakraV3.Text>-</ChakraV3.Text>
                ) : (
                  <ChakraV3.Wrap pt={2}>
                    {incident.labels.map((label) => {
                      return (
                        <ChakraV3.WrapItem key={label.id}>
                          <ChakraV3.Tag w='fit-content' size='sm' colorPalette={label.color}>
                            {label.name}
                          </ChakraV3.Tag>
                        </ChakraV3.WrapItem>
                      );
                    })}
                  </ChakraV3.Wrap>
                )}
              </ChakraV3.FormField>
            </FormStack>
          </FormSection>

          <ChakraV3.Separator />

          <FormSection heading='Incident info'>
            <FormStack orientation='horizontal'>
              <ChakraV3.FormField label='Occurred on'>
                <FancyDate date={incident.timestamp} direction='row' fontSize='sm' />
              </ChakraV3.FormField>
              <ChakraV3.FormField label='Occurred at'>
                <ChakraV3.Text fontSize='sm'>{incident.setting}</ChakraV3.Text>
              </ChakraV3.FormField>
            </FormStack>
            <FormStack orientation='horizontal'>
              <ChakraV3.FormField label='Participant involved'>
                {incident.participant ? (
                  <UserDescription
                    name={renderParticipantName(incident.participant)}
                    secondary={incident.participant.ndisNumber ?? undefined}
                  />
                ) : (
                  <ChakraV3.Text fontSize='sm'>No participant</ChakraV3.Text>
                )}
              </ChakraV3.FormField>
            </FormStack>
            <FormStack orientation='horizontal'>
              <ChakraV3.FormField label='Description'>
                <ChakraV3.Text whiteSpace='pre-wrap' fontSize='sm'>
                  {incident.description}
                </ChakraV3.Text>
              </ChakraV3.FormField>
            </FormStack>
          </FormSection>

          <ChakraV3.Separator />

          <FormSection heading='Witnesses'>
            <FormStack orientation='vertical'>{witnessesContent}</FormStack>
          </FormSection>

          {reviewContentSummary}

          <ChakraV3.Separator />
          <ChakraV3.Spacer />
          <ChakraV3.Button
            onClick={() => showModal(IncidentEditModal, { incidentId: incident.id })}
            w='full'
            size='sm'
            disabled={isReviewed}
            variant='subtle'
          >
            <FaPencilAlt />
            Edit incident
          </ChakraV3.Button>
        </ChakraV3.Stack>
      </ChakraV3.Card.Body>
    </ChakraV3.Card.Root>
  );
};
